import documentPanelSignal from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import { setDocumentFullscreen } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentFullscreenSignal';
import useDocumentFullscreen from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentFullscreen';
import useFullscreenPort from '@UI/Providers/PortsProvider/Hooks/Fullscreen/useFullscreenPort';
import { useSignalValue } from '@verclam/pulsar';
import * as React from 'react';

const OPEN_DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';

/**
 * Mounted once by the screen: follows an exit decided by the browser (unless a sidebar is open, where the layout stays), leaves the browser's fullscreen
 * whenever the mode ends or the screen unmounts, locks the page scroll under the document, and maps
 * Échap to the exit when neither the sidebar nor a dialog is open.
 */
const useFullscreenSync = (): void => {
	const port = useFullscreenPort();
	const { isFullscreen, exit } = useDocumentFullscreen();
	const panel = useSignalValue(documentPanelSignal);
	const isPanelOpen = panel !== null;

	React.useEffect(() => {
		return port.subscribe((isActive) => {
			// The browser consumes Échap to leave its fullscreen: with a sidebar open, the layout stays.
			if (!isActive && documentPanelSignal.peek() === null) {
				setDocumentFullscreen(false);
			}
		});
	}, [port]);

	React.useEffect(() => {
		if (!isFullscreen && port.isActive()) {
			void port.exit();
		}
	}, [isFullscreen, port]);

	React.useEffect(() => {
		return () => {
			if (port.isActive()) {
				void port.exit();
			}
		};
	}, [port]);

	React.useEffect(() => {
		if (!isFullscreen) {
			return undefined;
		}

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isFullscreen]);

	React.useEffect(() => {
		if (!isFullscreen || isPanelOpen) {
			return undefined;
		}

		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.key !== 'Escape' || event.defaultPrevented) {
				return;
			}

			if (document.querySelector(OPEN_DIALOG_SELECTOR) !== null) {
				return;
			}

			exit();
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isFullscreen, isPanelOpen, exit]);
};

export default useFullscreenSync;
